import React, { useEffect, useRef, useState } from "react";

/**
 * LivingSectionTitle — premium scroll-triggered title reveal.
 *
 * Motion philosophy:
 * - "Word reveal" for long titles, "letter reveal" for short ones, picked
 *   automatically when `variant` is "signal". The math is simple: titles
 *   ≤ 22 characters get letters, longer titles get words. This avoids the
 *   "long-text-feels-slow" trap.
 * - We use IntersectionObserver (one-shot) rather than scroll-sync because:
 *   (a) it never janks on lower-end devices, (b) it never replays
 *   distractingly, (c) the page already runs the particle cursor + LogoAura
 *   + 3D book parallax + Hero alignment animation, so we explicitly keep
 *   this off the scroll-listener pipeline.
 * - GPU-friendly: only `opacity` + `transform: translate3d`. No filters,
 *   no background-clip, no blur — those conflict with the particle
 *   cursor's mix-blend-mode and were the root cause of the earlier nav
 *   shimmer bug.
 *
 * Variants:
 *   - "signal"   — default, soft lift + opacity, stagger per word or letter
 *   - "flow"     — slight horizontal slide-in (right-to-left)
 *   - "quiet"    — fade only, no transform (use for editorial pages)
 *   - "goldline" — same as signal + a gold underline that draws in
 *
 * Props:
 *   text       — title text, may contain `<em>` segments via `parts` prop
 *   parts      — optional alternative: array of { text, italic } chunks
 *                to support italic + gold-gradient inline segments
 *   eyebrow    — small caps label rendered above title
 *   align      — "left" | "center"
 *   variant    — "signal" | "flow" | "quiet" | "goldline"
 *   triggerMode— "viewport" (default) | "immediate"
 *   stagger    — ms between units (default 55ms)
 *   className  — passthrough for outer wrapper
 *   as         — heading element ("h1" | "h2" | "h3"), default "h2"
 */
const splitToUnits = (text, mode) => {
  if (mode === "letters") {
    // Split into characters but keep words groupable so we can preserve
    // word-break behavior (no line-breaks inside a word).
    return text.split(/(\s+)/).map((chunk, idx) => ({
      type: chunk.trim() === "" ? "space" : "word",
      key: `c-${idx}`,
      chars: chunk.trim() === "" ? null : [...chunk],
    }));
  }
  return text.split(/(\s+)/).map((chunk, idx) => ({
    type: chunk.trim() === "" ? "space" : "word",
    key: `w-${idx}`,
    text: chunk,
  }));
};

const LivingSectionTitle = ({
  text,
  parts = null,
  eyebrow = null,
  align = "center",
  variant = "signal",
  triggerMode = "viewport",
  stagger = 55,
  className = "",
  as: Heading = "h2",
}) => {
  const wrapperRef = useRef(null);
  const [shown, setShown] = useState(triggerMode === "immediate");

  useEffect(() => {
    if (triggerMode === "immediate") return undefined;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShown(true);
      return undefined;
    }
    const el = wrapperRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [triggerMode]);

  // Pick split mode: letters for short titles, words otherwise.
  const splitMode = parts
    ? "parts"
    : (text || "").length <= 22
    ? "letters"
    : "words";

  // Build the rendered units. For `parts` mode we render each chunk as
  // its own word block (still staggered) and let consumers mark italic
  // chunks for gold-gradient styling.
  let units = [];
  if (parts) {
    units = parts.map((p, i) => ({
      type: "chunk",
      key: `p-${i}`,
      text: p.text,
      italic: !!p.italic,
    }));
  } else {
    units = splitToUnits(text, splitMode === "letters" ? "letters" : "words");
  }

  // Stagger index counter. Spaces don't consume stagger time so word
  // groupings feel natural.
  let staggerIdx = 0;
  const nextDelay = () => {
    const ms = staggerIdx * stagger;
    staggerIdx += 1;
    return ms;
  };

  const classes = [
    "lst",
    `lst--${variant}`,
    `lst--${align}`,
    shown ? "is-in" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={wrapperRef} className={classes} data-testid="living-section-title">
      {eyebrow ? (
        <p className="lst-eyebrow" style={{ transitionDelay: `${nextDelay()}ms` }}>
          {eyebrow}
        </p>
      ) : null}
      <Heading className="lst-heading">
        {parts
          ? units.map((u) => (
              <span
                key={u.key}
                className={`lst-word ${u.italic ? "is-italic" : ""}`}
                style={{ transitionDelay: `${nextDelay()}ms` }}
              >
                {u.italic ? (
                  <span className="italic gold-gradient-text">{u.text}</span>
                ) : (
                  u.text
                )}{" "}
              </span>
            ))
          : units.map((u) => {
              if (u.type === "space") return <span key={u.key}> </span>;
              if (splitMode === "letters") {
                return (
                  <span key={u.key} className="lst-word">
                    {u.chars.map((ch, ci) => (
                      <span
                        key={ci}
                        className="lst-char"
                        style={{ transitionDelay: `${nextDelay()}ms` }}
                      >
                        {ch}
                      </span>
                    ))}
                  </span>
                );
              }
              return (
                <span
                  key={u.key}
                  className="lst-word"
                  style={{ transitionDelay: `${nextDelay()}ms` }}
                >
                  {u.text}
                </span>
              );
            })}
      </Heading>
      {variant === "goldline" ? <span className="lst-underline" aria-hidden /> : null}
    </div>
  );
};

export default LivingSectionTitle;
